import { useEffect, useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import TextareaAutosize from "react-textarea-autosize";

import { twMerge } from "@/utils/twMerge";

interface Props {
  title: string;
  isEditing?: boolean;
  type: "folder" | "document" | "chat";
  onUpdate?: (title: string) => void;
  onStopEdit?: () => void;
  className?: string;
}

export const ListItemTitle = observer(function ListItemTitle(props: Props) {
  const { title, className } = props;
  const [editTitle, setEditTitle] = useState(title);
  const t = useTranslations();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const parsedTitle = title;

  const stopEdit = (shouldUpdate: boolean) => {
    if (shouldUpdate && editTitle !== props.title && props.onUpdate) {
      props.onUpdate(editTitle);
    } else {
      setEditTitle(title);
    }
    props.onStopEdit?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditTitle(e.target.value);
  };

  const handleBlur = () => {
    stopEdit(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      stopEdit(true);
    } else if (e.key === "Escape") {
      stopEdit(false);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // This prevents toggling folder on Spacebar pressing in chrome browser
    e.preventDefault();
  };

  useEffect(() => {
    if (props.isEditing && textAreaRef.current) {
      // TODO: doesn't work on real iPhone
      textAreaRef.current?.focus();
      textAreaRef.current?.select();
    }
  }, [props.isEditing]);

  if (props.isEditing) {
    return (
      <TextareaAutosize
        ref={textAreaRef}
        aria-label={t("documentTree.listItem")}
        value={editTitle}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        className={twMerge(
          "flex-1 py-[5px]",
          "outline-none border-none bg-transparent resize-none",
          "font-geist text-[16px] leading-[20px] tracking-tight text-left",
          "sm:py-[9px] sm:text-[17px] sm:leading-[20px]",
          props.type === "folder" && "font-medium",
        )}
        autoFocus // TODO: doesn't work on real iPhone
      />
    );
  }
  return (
    <div
      className={twMerge(
        "truncate",
        "py-[5px]",
        "font-geist text-[16px] leading-[20px] tracking-tight text-left",
        "sm:py-[9px] sm:text-[17px] sm:leading-[20px]",
        props.type === "folder" && "font-medium",
        className,
      )}
    >
      {parsedTitle ||
        t(props.type === "chat" ? "chat.untitledChat" : "doc.documentTitle.placeholder")}
    </div>
  );
});
